/**
 * Phase 21.3 — runtime feature flag client.
 *
 * The backend resolves overrides + preset defaults; the client combines that
 * with the in-process feature registry's defaults to produce the final
 * boolean per `useFeature(id)` call.
 *
 * Resolution at the client (mirrors server precedence):
 *   1. Server-resolved value (from `GET /features`) → preset / overrides
 *      (and, since Phase 64, an explicit `false` for every feature whose
 *      suite the org's plan doesn't entitle)
 *   2. `featureRegistry.getFeature(id).default` → baseline default
 *   3. `false` if neither is known (unknown feature id)
 *
 * The store is intentionally just a setter + a snapshot; fetching is the
 * consuming app's job. Each surface (RN POS, dashboard, Web POS) wires its
 * own React Query hook to call `setResolvedFeatures(map)` after a successful
 * fetch / WebSocket invalidation.
 */
import { create } from 'zustand';
import type { SuiteId } from '@commerce-os/shared-types';
import { getFeature } from '../featureRegistry';

interface FeatureFlagState {
  /** Server-resolved map; sparse — only IDs the server saw via overrides,
   *  the vertical preset or a plan lock will appear. */
  resolved: Record<string, boolean>;
  /**
   * Phase 64 — the org's entitled suites from `GET /features`, or `null`
   * while unknown (not fetched yet, or an older API that doesn't send it).
   * Only used to explain an OFF feature — the resolved map already carries
   * plan locks as `false`, so this never turns anything on or off.
   */
  entitledSuites: readonly SuiteId[] | null;
  /** Last sync timestamp in ms — surfaces in dev tools / debug. */
  lastSyncedAtMs: number | null;
  setResolvedFeatures: (
    map: Record<string, boolean>,
    entitledSuites?: readonly SuiteId[] | null,
  ) => void;
  reset: () => void;
}

export const useFeatureFlagStore = create<FeatureFlagState>((set) => ({
  resolved: {},
  entitledSuites: null,
  lastSyncedAtMs: null,
  setResolvedFeatures: (map, entitledSuites) =>
    set({
      resolved: { ...map },
      entitledSuites: entitledSuites ? [...entitledSuites] : null,
      lastSyncedAtMs: Date.now(),
    }),
  reset: () => set({ resolved: {}, entitledSuites: null, lastSyncedAtMs: null }),
}));

/**
 * Pure resolver — compose server map + registry default. Exported so
 * non-React callers (orchestrator code, tests, middleware) can check
 * features without going through the React hook.
 */
export function isFeatureEnabled(
  featureId: string,
  resolved: Record<string, boolean>,
): boolean {
  if (Object.prototype.hasOwnProperty.call(resolved, featureId)) {
    return resolved[featureId];
  }
  const def = getFeature(featureId);
  return def?.default ?? false;
}

/**
 * Phase 64 — the suite that keeps `featureId` off because the org's plan
 * doesn't include it, or `null` when the plan isn't the reason (the suite
 * is entitled, the feature is unregistered, or the entitled set is still
 * unknown). Surfaces use it to offer an upgrade instead of "ask an owner to
 * turn it on" — an owner can't turn on what the plan doesn't include.
 */
export function planLockedSuite(
  featureId: string,
  entitledSuites: readonly SuiteId[] | null | undefined,
): SuiteId | null {
  if (!entitledSuites) return null;
  const suite = getFeature(featureId)?.suite;
  if (!suite || entitledSuites.includes(suite)) return null;
  return suite;
}

/**
 * React hook: returns the resolved boolean for `featureId`. Re-renders
 * when the resolved map changes. Cheap; safe to call from any component.
 */
export function useFeature(featureId: string): boolean {
  const resolved = useFeatureFlagStore((s) => s.resolved);
  return isFeatureEnabled(featureId, resolved);
}

/** Non-React snapshot accessor. */
export function getFeatureSnapshot(featureId: string): boolean {
  return isFeatureEnabled(featureId, useFeatureFlagStore.getState().resolved);
}
